import {
  AbsoluteCenter,
  Box,
  Button,
  EmptyState,
  Flex,
  HStack,
  IconButton,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { type GroupingState, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineFolderOpen } from 'react-icons/ai';
import { BiTime } from 'react-icons/bi';
import { BsGear } from 'react-icons/bs';
import { FaRegCalendar } from 'react-icons/fa';

import { IndicatorsService } from '@/client/index.ts';
import Loader from '@/components/Loader.tsx';
import IndicatorDrawer from '@/components/indicators/index/drawer/IndicatorDrawer.tsx';
import { Tooltip } from '@/components/ui/tooltip';
import { Route } from '@/routes/_layout/$slug/indicators/collecte/index.tsx';
import { generateCollecteName, generatePeriodMonths, stringToColor } from '@/utils.ts';

function getIndicatorsPeriodsCollecteQueryOptions({
  slug,
  page,
  pageSize,
  filter,
  sort,
  search,
}: {
  slug: string;
  entity_id: string;
  page: number;
  pageSize: number;
  filter: string;
  sort: string;
  search: string;
  group_by: string;
}) {
  return {
    queryKey: ['indicators_periods_collecte', { page, pageSize, filter, sort, search }],
    queryFn: async () => {
      return await IndicatorsService.readIndicatorsProgression({
        companySlug: slug,
      });
    },
  };
}

export function CollectePeriodTable() {
  const navigate = useNavigate({ from: Route.fullPath });

  const { t } = useTranslation();

  const { year, period, entity_id, page, pageSize, filter, sort, search, group_by } = Route.useSearch();
  const { slug } = useParams({ strict: false });
  /* const [count, setCount] = useState<number>(0); */
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [indicatorDetailsId, setIndicatorDetailsId] = useState<string | null>(null);

  const {
    data: indicatorsPeriodsData,
    isLoading,
    refetch,
  } = useQuery({
    ...getIndicatorsPeriodsCollecteQueryOptions({
      slug,
      entity_id,
      page,
      pageSize,
      filter,
      sort,
      search,
      group_by,
    }),
  });

  useEffect(() => {
    if (grouping.length === 0 && group_by !== '') setGrouping([group_by]);
    else if (grouping.length > 0 && grouping[0] !== group_by) {
      setGrouping([group_by]);
    }
    refetch();
  }, [year, period, entity_id, page, filter, pageSize, sort, search, group_by]);

  const columns = [
    {
      accessorKey: 'year_label',
      header: 'Intitulé des années',
      cell: ({ row }: { row: any }) => (
        <HStack>
          <IconButton
            variant="ghost"
            size="2xs"
            color={'white'}
            _hover={{ color: 'white' }}
            padding={'1rem 0.5rem'}
            bg={stringToColor(generateCollecteName(row.original.year, row.original.period_number, row.original.period))}
          >
            <FaRegCalendar />
          </IconButton>
          {row.original.reference_year === true && (
            <Tooltip
              showArrow
              content={t('collection.manage.columnsHeader.referenceYear')}
              contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
              openDelay={100}
              closeDelay={100}
            >
              <span style={{ cursor: 'pointer' }}>✨</span>
            </Tooltip>
          )}
          <Box>
            <Text fontWeight={'600'}>
              {generateCollecteName(row.original.year, row.original.period_number, row.original.period)}
            </Text>
          </Box>
        </HStack>
      ),
    },
    {
      accessorKey: 'period_months',
      header: 'Periodes',
      cell: ({ row }: { row: any }) => {
        return (
          <HStack>
            <BiTime />
            <Text color={'gray.500'}>{generatePeriodMonths(row.original.period_number, row.original.period)}</Text>
          </HStack>
        );
      },
    },
    {
      accessorKey: 'indicator_count',
      header: "Nombres d'indicateurs",
      cell: ({ row }: { row: any }) => {
        return (
          <HStack>
            <BiTime />
            <Text color={'gray.500'}>
              {row.original.count_indicators} {t('sidebar.indicators')}
            </Text>
          </HStack>
        );
      },
    },

    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }: { row: any }) => {
        const redirectYearCollecte = () => {
          const { year, period_number, entity_id, period } = row.original;

          navigate({
            to: '/$slug/indicators/collecte/$year',
            params: { slug, year },
            search: {
              period: `${period}${period_number}`,
              entity_id: entity_id || '',
              page: 1,
              pageSize: 25,
              filter: '',
              sort: '',
              group_by: '',
              search: '',
            },
          });
        };

        const redirectYearSetting = () => {
          const { year, period_number, entity_id, period } = row.original;

          navigate({
            to: '/$slug/indicators/collecte/$year/manage',
            params: { slug, year },
            search: {
              year: String(year),
              period: `${period}${period_number}`,
              entity_id: entity_id || '',
              page: 1,
              pageSize: 25,
              filter: '',
              sort: '',
              group_by: '',
              search: '',
            },
          });
        };

        return (
          <Flex columnGap={3}>
            <Button
              borderColor={'primary.500'}
              bgColor={'transparent'}
              _hover={{ color: 'white' }}
              color={'primary.500'}
              onClick={redirectYearCollecte}
            >
              <AiOutlineFolderOpen /> Collecter
            </Button>

            <Button bg={'transparent'} _hover={{ color: 'white' }} color={'primary.500'} onClick={redirectYearSetting}>
              <BsGear /> Paramétrer
            </Button>
          </Flex>
        );
      },
    },
  ];

  const table = useReactTable({
    data: indicatorsPeriodsData?.data || [],
    // @ts-ignore
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {isLoading ? (
        <Loader loadingText={t('loading.indicators.collecte')} />
      ) : (
        <Box maxH={'calc(90vh - 40px)'} w={'100%'} borderColor={'transparent'}>
          <Table.ScrollArea maxH={'calc(92vh - 40px)'} whiteSpace={'break-spaces'}>
            <Table.Root size="md" w={'100%'} interactive stickyHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Header key={headerGroup.id} borderColor={'transparent'} w={'100%'}>
                  <Table.Row bg={'#F1F7FA'} w={'100%'}>
                    {headerGroup.headers.map((header) => (
                      <Table.ColumnHeader
                        key={header.id}
                        style={{ width: `${header.getSize()}px` }}
                        alignContent={'end'}
                        placeContent={'center'}
                        p={4}
                      >
                        <Box display={'flex'} flexDir={'column'}>
                          <Box color={{ base: 'gray.600', _dark: 'white' }} fontWeight={'semibold'} display={'flex'}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </Box>
                        </Box>
                      </Table.ColumnHeader>
                    ))}
                  </Table.Row>
                </Table.Header>
              ))}
              <Table.Body>
                {table.getRowModel().rows.map((row) => (
                  <Table.Row
                    key={row.id}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    mb={3}
                    bg="white"
                    _hover={{ shadow: 'md', bg: 'gray.50' }}
                    m={'1rem 0.5rem'}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell key={cell.id} p={4}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        </Box>
      )}
      <IndicatorDrawer
        indicatorId={indicatorDetailsId ?? undefined}
        isOpen={indicatorDetailsId !== null}
        setIsOpen={(state: boolean) => setIndicatorDetailsId(state ? indicatorDetailsId : null)}
        editDisabled={true}
      />
      {filter === '' && sort === '' && (
        <AbsoluteCenter>
          <EmptyState.Root>
            <EmptyState.Content>
              <VStack textAlign="center">
                <EmptyState.Title>{t('messages.hints.addCollecte.title')}</EmptyState.Title>
                <EmptyState.Description>
                  {t('messages.hints.addFirstCollecte', {
                    manageLabel: t('pageTitle.indicators.manage'),
                  })}
                </EmptyState.Description>
              </VStack>
            </EmptyState.Content>
          </EmptyState.Root>
        </AbsoluteCenter>
      )}
    </>
  );
}

export default CollectePeriodTable;
